import {
  JOB_TABS,
  formatJobNumber,
  groupByWorklist,
  jobListSchema,
  statusesInTab,
  tabOf,
  worklistStatus,
  type JobTabKey,
  type WorklistStatusKey,
} from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { SortMenu } from '../components/ListControls.js'
import { Icon } from '../components/Icon.js'
import { NewJobDialog } from '../components/NewJobDialog.js'
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  Page,
  PageHeader,
  Pagination,
  PRIORITY_TONE,
  SearchBox,
  Select,
  SkeletonRows,
  Toolbar,
  type Tone,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { addressLine, formatShortDate, money, relativeTime } from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'

/**
 * The Jobs list as a worklist.
 *
 * Three tabs, one per pipeline stage, and inside each a status derived from the
 * dates on the job rather than picked by hand — so each tab doubles as a to-do
 * list: who needs a quote sent, who needs booking in, who needs chasing for
 * payment. The rules live in `@fieldops/shared/job-worklist`, shared with the
 * API so the counts and the badges cannot disagree. See decision 35.
 *
 * `dueAt` is deliberately not shown. It is set on 0 of 68,359 jobs, so the
 * column it used to occupy said "—" on every row; the date each status actually
 * turns on is shown instead.
 */

/** Covers the schema's sort enum exactly, so every reachable sort has a label. */
const SORT_FIELDS = [
  { key: 'createdAt', label: 'Created' },
  { key: 'number', label: 'Job number' },
  { key: 'lastVisitAt', label: 'Booking date' },
  { key: 'status', label: 'Stage' },
  { key: 'totalIncTax', label: 'Total' },
] as const

/**
 * Each queue is a heading on the page field and a white card of rows.
 *
 * The heading (pill, count, meaning, the action as a label) stays on the
 * `ink-100` field so it reads as a divider between queues; the rows go in a
 * `.board-card` so a queue is a list you can see the edges of. Rows drawn
 * straight onto the field left every queue running into the page. Decision 49.
 *
 * The row is one link. "Book it in" used to look like a button that booked;
 * it opens the job, same as the rest of the row.
 *
 * The identity on the row is the client, the kind of work and one address
 * column — street and suburb together, not two fields. There is no service
 * title — `Job.title` is set on 1 of 74,300 jobs, so the category says what
 * the job is (rule 19). The number is sized for a prefixed `displayNumber` and
 * does not ellipsis. At a desk the row is one line and a long cell truncates
 * with its whole text on hover; on a phone text wraps.
 */
const ACTION_PILL: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  blue: 'bg-blue-50 text-blue-800',
  green: 'bg-brand-50 text-brand-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-50 text-red-800',
  purple: 'bg-purple-50 text-purple-800',
}

const DOT_COLOUR: Record<Tone, string> = {
  neutral: 'bg-ink-400',
  blue: 'bg-blue-600',
  green: 'bg-emerald-600',
  amber: 'bg-amber-500',
  red: 'bg-red-600',
  purple: 'bg-purple-600',
}

export function Jobs() {
  const trpc = useTRPC()
  const { can } = useSession()
  const [params, setParams] = useUrlState(jobListSchema)
  const [creating, setCreating] = useState(false)

  // Grouping is by derived status now, so the client no longer chooses it and
  // `group` never reaches the query. It stays in the schema because the URL
  // codec is schema-driven; see decision 33.
  const { group: _group, ...listParams } = params

  const { data, isPending, error, refetch } = useQuery(trpc.jobs.list.queryOptions(listParams))
  const { data: counts } = useQuery(
    trpc.jobs.worklistCounts.queryOptions({ deleted: params.deleted }),
  )

  // One instant labels the whole page, rather than each row asking the clock
  // again and a list drifting across the Scheduled / Follow-up boundary as it
  // renders.
  const now = useMemo(() => new Date(), [data])

  /**
   * A link written before the tabs existed carries statuses and no tab, so it
   * would open on the default tab with nothing in it. Move it to the tab those
   * statuses belong to. Changing tabs clears `status`, so this cannot fight a
   * deliberate choice.
   */
  const legacyTab = params.status.length > 0 ? tabOf(params.status[0]!) : undefined
  useEffect(() => {
    if (legacyTab && legacyTab !== params.tab) setParams({ tab: legacyTab }, { replace: true })
  }, [legacyTab, params.tab, setParams])

  const groups = useMemo(
    () => groupByWorklist(data?.items ?? [], params.tab, now),
    [data?.items, params.tab, now],
  )

  const filtered =
    Boolean(params.q) ||
    Boolean(params.worklist) ||
    Boolean(params.branchId) ||
    params.assigneeId.length > 0 ||
    params.status.length > 0

  const tabStatuses = statusesInTab(params.tab)
  const needing = counts
    ? tabStatuses
        .filter((key) => worklistStatus(key).action)
        .reduce((sum, key) => sum + counts.byStatus[key], 0)
    : 0
  const inTab = counts?.byTab[params.tab]
  const tabLabel = JOB_TABS.find((entry) => entry.key === params.tab)?.label ?? 'Jobs'

  return (
    <Page width="xwide" field>
      <PageHeader
        title="Jobs"
        description={
          inTab === undefined
            ? `${tabLabel} stage`
            : `${formatCount(inTab)} in ${tabLabel.toLowerCase()} · ${formatCount(needing)} need action`
        }
        actions={
          can('JOB_EDIT') ? (
            <Button
              variant="primary"
              className="rounded-full"
              onClick={() => setCreating(true)}
              icon={<Icon name="plus" className="size-4" />}
            >
              New job
            </Button>
          ) : null
        }
      />

      <Card variant="board">
        <Toolbar className="border-b-0">
          <TabBar
            tab={params.tab}
            counts={counts?.byTab}
            onChange={(tab) => setParams({ tab, worklist: undefined, status: [] })}
          />

          <span className="mx-0.5 hidden h-6 w-px bg-ink-200 lg:block" />

          <div className="min-w-0 w-full sm:w-[260px] sm:flex-none">
            <SearchBox
              value={params.q ?? ''}
              onChange={(q) => setParams({ q: q || undefined }, { replace: true })}
              placeholder="Search jobs, clients or addresses"
              className="w-full rounded-lg sm:w-full"
            />
          </div>

          <BranchFilter
            value={params.branchId}
            onChange={(branchId) => setParams({ branchId, page: 1 })}
          />

          <TechnicianFilter
            value={params.assigneeId}
            onChange={(assigneeId) => setParams({ assigneeId, page: 1 })}
          />

          <ButtonGroup label="How the list reads" className="ml-auto rounded-lg">
            <SortMenu
              fields={SORT_FIELDS}
              sort={params.sort}
              dir={params.dir}
              onChange={(next) => setParams(next)}
            />
          </ButtonGroup>

          {filtered ? (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() =>
                setParams({
                  q: undefined,
                  worklist: undefined,
                  branchId: undefined,
                  assigneeId: [],
                  status: [],
                  page: 1,
                })
              }
            >
              Clear
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      <ChipRow
        tab={params.tab}
        selected={params.worklist}
        counts={counts?.byStatus}
        needing={needing}
        onChange={(worklist) => setParams({ worklist })}
      />

      {error ? (
        <Card variant="board">
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        </Card>
      ) : isPending ? (
        <Card variant="board">
          <SkeletonRows />
        </Card>
      ) : data.items.length === 0 ? (
        <Card variant="board">
          <EmptyState
            title={filtered ? 'No jobs match' : 'Nothing here'}
            description={
              filtered ? 'Try widening the filters.' : `No jobs are in the ${tabLabel} stage.`
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((entry) => {
            const list = (
              <Card variant="board">
                <ul className="divide-y divide-ink-100">
                  {entry.items.map((job) => (
                    <li key={job.id}>
                      <JobRow job={job} status={entry.key} now={now} />
                    </li>
                  ))}
                </ul>
              </Card>
            )

            // The chip already names the queue, so a second header repeating
            // it is noise. The mixed All view keeps the heading so you can
            // scan; both views draw the rows in their own card.
            if (params.worklist === entry.key) {
              return <section key={entry.key}>{list}</section>
            }

            return (
              <WorklistGroup
                key={entry.key}
                status={entry.key}
                shown={entry.items.length}
                total={counts?.byStatus[entry.key]}
                onSelect={() => setParams({ worklist: entry.key })}
              >
                {list}
              </WorklistGroup>
            )
          })}
        </div>
      )}

      {data ? (
        <Pagination
          page={data.page}
          pageCount={data.pageCount}
          total={data.total}
          onChange={(page) => setParams({ page })}
        />
      ) : null}

      <NewJobDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  )
}

/**
 * Branch, as a filter.
 *
 * Hidden entirely on a single-branch account rather than showing a picker with
 * one option in it — the business has two branches today and might have one
 * tomorrow, and a dead control is worse than none.
 */
function BranchFilter({
  value,
  onChange,
}: {
  value: string | undefined
  onChange: (branchId: string | undefined) => void
}) {
  const trpc = useTRPC()
  const { data } = useQuery(trpc.settings.branches.queryOptions())

  if (!data || data.length < 2) return null

  return (
    <Select
      aria-label="Branch"
      className="w-full shrink-0 rounded-lg sm:w-[160px]"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value || undefined)}
    >
      <option value="">All branches</option>
      {data.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
    </Select>
  )
}

/**
 * Technician, as a filter. `assigneeId` is an array in the schema, but one
 * name at a time is what the toolbar has room for and what people ask for.
 */
function TechnicianFilter({
  value,
  onChange,
}: {
  value: string[]
  onChange: (assigneeId: string[]) => void
}) {
  const trpc = useTRPC()
  const { can } = useSession()
  const { data } = useQuery({
    ...trpc.users.schedulable.queryOptions(),
    enabled: can('SCHEDULE_VIEW'),
  })

  if (!data || data.length === 0) return null

  return (
    <Select
      aria-label="Technician"
      className="w-full shrink-0 rounded-lg sm:w-[180px]"
      value={value[0] ?? ''}
      onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
    >
      <option value="">Any technician</option>
      {data.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Select>
  )
}

/**
 * The three pipeline stages. A job is in exactly one at a time.
 *
 * Drawn as the board's segmented pill rather than a browser tab strip: it is
 * the same control as the day / timeline / week / map switcher on the schedule,
 * it sits in the toolbar instead of eating a row of its own, and the caption
 * each tab used to carry underneath becomes its tooltip.
 */
function TabBar({
  tab,
  counts,
  onChange,
}: {
  tab: JobTabKey
  counts: Record<JobTabKey, number> | undefined
  onChange: (tab: JobTabKey) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Job stage"
      className="flex w-full items-center gap-0.5 rounded-full bg-ink-100 p-0.5 sm:w-auto sm:shrink-0"
    >
      {JOB_TABS.map((entry) => {
        const selected = entry.key === tab
        return (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={selected}
            title={entry.caption}
            onClick={() => onChange(entry.key)}
            className={cn(
              'flex min-h-[var(--control-sm)] flex-1 items-center justify-center gap-1.5 rounded-full px-3 text-xs whitespace-nowrap transition-colors sm:flex-none',
              selected
                ? 'bg-brand-800 font-semibold text-white'
                : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
            )}
          >
            {entry.label}
            {/* The count is the first thing to go: three labelled pills and
                three five-digit counts do not both fit across a handset. */}
            <span
              className={cn(
                'hidden text-[11px] tabular-nums sm:inline',
                selected ? 'text-white/70' : 'text-ink-400',
              )}
            >
              {formatCount(counts?.[entry.key])}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** The tab's derived statuses, as filters. Counts are over every job. */
function ChipRow({
  tab,
  selected,
  counts,
  needing,
  onChange,
}: {
  tab: JobTabKey
  selected: WorklistStatusKey | undefined
  counts: Record<WorklistStatusKey, number> | undefined
  needing: number
  onChange: (status: WorklistStatusKey | undefined) => void
}) {
  const statuses = statusesInTab(tab)
  const total = counts ? statuses.reduce((sum, key) => sum + counts[key], 0) : undefined

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chip active={!selected} count={total} onClick={() => onChange(undefined)}>
        All
      </Chip>
      {statuses.map((key) => (
        <Chip
          key={key}
          active={selected === key}
          count={counts?.[key]}
          tone={worklistStatus(key).tone}
          onClick={() => onChange(selected === key ? undefined : key)}
        >
          {worklistStatus(key).label}
        </Chip>
      ))}
      {needing > 0 ? (
        <span className="ml-auto hidden text-[11px] tabular-nums text-ink-400 sm:block">
          {formatCount(needing)} need action
        </span>
      ) : null}
    </div>
  )
}

/**
 * A derived status, as a filter. `FilterChip` carries the shape and the pressed
 * state so the queue chips here and the board's own chips cannot drift; the dot
 * and the count are what this list adds.
 */
function Chip({
  active,
  count,
  tone,
  onClick,
  children,
}: {
  active: boolean
  count: number | undefined
  tone?: Tone
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <FilterChip active={active} onClick={onClick} className="gap-1.5 px-2.5">
      {tone ? <span className={cn('size-1.5 shrink-0 rounded-full', DOT_COLOUR[tone])} /> : null}
      <span>{children}</span>
      <span className={cn('tabular-nums', active ? 'text-white/70' : 'text-ink-400')}>
        {formatCount(count)}
      </span>
    </FilterChip>
  )
}

/**
 * A queue: its heading on the page field, its rows in the card underneath.
 *
 * The count is the total across every job, not the number on this page, which
 * is the point of `jobs.worklistCounts`. The header is a button so narrowing to
 * just this status — and so paginating within it — is one click. The action
 * pill on the right is the status's `action` string; it is not a second
 * control.
 */
function WorklistGroup({
  status,
  shown,
  total,
  onSelect,
  children,
}: {
  status: WorklistStatusKey
  shown: number
  total: number | undefined
  onSelect: () => void
  children: React.ReactNode
}) {
  const entry = worklistStatus(status)
  const count = total ?? shown
  const partial = total !== undefined && shown < total
  const headerClass = cn(
    'flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-1.5 py-1 text-left',
    partial && 'cursor-pointer hover:bg-white/70',
  )
  const header = (
    <>
      <Badge tone={entry.tone}>{entry.label}</Badge>
      <span className="min-w-0 flex-1 text-sm text-ink-500">
        <span className="font-semibold tabular-nums text-ink-800">{formatCount(count)}</span>
        {` ${count === 1 ? 'job' : 'jobs'} ${entry.meaning}`}
      </span>
      {/* The action the queue is waiting on stays on the heading even when the
          heading is also the "Show only" button: with global counts nearly
          every queue is partial, and hiding the action behind that made the
          pill something you never saw. */}
      <span className="ml-auto flex items-center gap-2">
        {entry.action ? (
          <span
            className={cn(
              // A phone row already carries the action; the heading's copy
              // was what wrapped the heading onto three lines.
              'rounded-full px-2 py-0.5 text-[10px] font-bold max-sm:hidden',
              ACTION_PILL[entry.tone],
            )}
          >
            {entry.action}
          </span>
        ) : null}
        {partial ? <span className="text-[11px] font-medium text-ink-500">Show only</span> : null}
      </span>
    </>
  )

  return (
    <section className="flex flex-col gap-1">
      {partial ? (
        <button
          type="button"
          onClick={onSelect}
          title={`Show only ${entry.label}`}
          className={headerClass}
        >
          {header}
        </button>
      ) : (
        <div className={headerClass}>{header}</div>
      )}
      {children}
    </section>
  )
}

type JobListItem = RouterOutputs['jobs']['list']['items'][number]

function JobRow({
  job,
  status,
  now,
}: {
  job: JobListItem
  status: WorklistStatusKey
  now: Date
}) {
  const jobHref = useJobHref()
  const [when, whenSub] = worklistWhen(job, status, now)
  const place = jobPlace(job)
  const urgent = job.priority === 'HIGH' || job.priority === 'EMERGENCY'
  const entry = worklistStatus(status)

  // One set of cells for both widths. At a desk the row is one line: number,
  // client, kind of work, address, the date the status turns on, total and the
  // action, each cell truncating with the whole text on hover, so a screen
  // holds twice the jobs it did when every row was two lines. On a phone they
  // reflow into three lines — number, client and total; the address; the date
  // and the action — because four fixed widths summed to more than a handset
  // and left the client column a few pixels wide, wrapping the address one
  // letter per line.
  return (
    <Link
      to={jobHref(job.id)}
      className="group flex flex-wrap items-center gap-x-3 gap-y-1 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50 sm:flex-nowrap"
    >
      <span className="shrink-0 text-xs whitespace-nowrap tabular-nums text-ink-500 sm:w-[5.75rem]">
        {formatJobNumber(job)}
      </span>

      <span className="min-w-0 flex-1 sm:flex-[1.1]">
        <span className="flex min-w-0 items-center gap-1.5 max-sm:flex-wrap">
          <span
            className="min-w-0 text-sm font-medium break-words text-ink-900 sm:truncate"
            title={job.client.name}
          >
            {job.client.name}
          </span>
          {urgent ? (
            <Badge tone={PRIORITY_TONE[job.priority]} className="shrink-0">
              {humanisePriority(job.priority)}
            </Badge>
          ) : null}
        </span>
      </span>

      <span
        className="hidden min-w-0 items-center gap-1.5 text-xs text-ink-600 sm:flex sm:w-[9.5rem] sm:shrink-0"
        title={job.category?.name}
      >
        {job.category ? (
          <>
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: job.category.colour }}
            />
            <span className="truncate">{job.category.name}</span>
          </>
        ) : (
          <span className="text-ink-400">No kind of work</span>
        )}
      </span>

      <span
        className="min-w-0 text-sm break-words text-ink-700 max-sm:order-4 max-sm:basis-full max-sm:text-xs max-sm:text-ink-600 sm:flex-[1.2] sm:truncate"
        title={place}
      >
        {place}
      </span>

      <span
        className="min-w-0 max-sm:order-5 max-sm:flex-1 sm:w-[17rem] sm:shrink-0 sm:truncate"
        title={`${when} · ${whenSub}`}
      >
        <span className="text-xs tabular-nums text-ink-700 max-sm:text-ink-500">{when}</span>
        <span className="text-[11px] text-ink-400 max-sm:hidden"> · {whenSub}</span>
      </span>

      <span
        className={cn(
          'shrink-0 text-right text-sm font-medium tabular-nums text-ink-900 sm:w-[5.5rem]',
          !job.totalIncTax && 'max-sm:hidden',
        )}
      >
        {job.totalIncTax ? money(job.totalIncTax) : '—'}
      </span>

      <span className="flex shrink-0 items-center justify-end gap-2 max-sm:contents sm:w-[7.5rem]">
        {entry.action ? (
          <span className="rounded-md bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 transition-colors group-hover:bg-brand-600 group-hover:text-white max-sm:order-6">
            {entry.action}
          </span>
        ) : null}
        {job.assignee ? (
          <Avatar name={job.assignee.name} colour={job.assignee.colour} size="sm" />
        ) : null}
      </span>
    </Link>
  )
}

function humanisePriority(priority: string): string {
  return priority === 'EMERGENCY' ? 'Emergency' : 'High'
}

/**
 * The date that put the job in this status, and how long it has sat there.
 *
 * Each status is waiting on a different clock, so a single "date" column would
 * be meaningless: a draft quote cares how old it is, an unpaid invoice cares
 * how overdue it is.
 */
function worklistWhen(job: JobListItem, status: WorklistStatusKey, now: Date): [string, string] {
  const nextVisit = job.visits.find((visit) => new Date(visit.startAt) > now)
  const visits = job.visits.length

  switch (status) {
    case 'DRAFT':
      return [`Created ${formatShortDate(job.createdAt)}`, `Raised ${relativeTime(job.createdAt)}`]
    case 'PRICED':
      return [`Created ${formatShortDate(job.createdAt)}`, 'Priced, not sent']
    case 'AWAITING_REPLY':
      return [`Sent ${formatShortDate(job.quoteSentAt)}`, `${relativeTime(job.quoteSentAt)}, no reply`]
    case 'CLOSED_LOST':
      return [`Created ${formatShortDate(job.createdAt)}`, 'Kept for the record']
    case 'UNSCHEDULED': {
      // Parked and untriaged are both honestly Unscheduled, and the row would
      // otherwise read the same for a job somebody deliberately filed under
      // "Annual termites" and one nobody has opened yet. The bucket name says
      // which, in the line that already explains why the job is here.
      const since: [string, string] = job.quoteSentAt
        ? [`Quoted ${formatShortDate(job.quoteSentAt)}`, `Unbooked since ${relativeTime(job.quoteSentAt)}`]
        : [`Created ${formatShortDate(job.createdAt)}`, `Never booked · ${relativeTime(job.createdAt)}`]

      return job.dispatchBucket ? [since[0], `Parked · ${job.dispatchBucket.name}`] : since
    }
    case 'SCHEDULED':
      return [
        `Booked ${formatShortDate(nextVisit?.startAt ?? job.lastVisitAt)}`,
        relativeTime(nextVisit?.startAt ?? job.lastVisitAt),
      ]
    case 'FOLLOW_UP':
      return [
        `Last visit ${formatShortDate(job.lastVisitAt)}`,
        `${relativeTime(job.lastVisitAt)} · ${visits} ${visits === 1 ? 'visit' : 'visits'}`,
      ]
    case 'INVOICE_PENDING':
      return [
        `Completed ${formatShortDate(job.completedAt)}`,
        `Uninvoiced since ${relativeTime(job.completedAt)}`,
      ]
    case 'AWAITING_PAYMENT':
      return [
        `Invoiced ${formatShortDate(job.invoiceSentAt)}`,
        `Outstanding ${relativeTime(job.invoiceSentAt)}`,
      ]
    case 'PAID':
      // There is no payment date to show: `Payment` is empty and `amountPaid`
      // is zero on every invoice the migration marked paid, so the invoice's
      // own status is all that is known.
      return [`Invoiced ${formatShortDate(job.invoiceSentAt)}`, 'Paid in full']
  }
}

function formatCount(value: number | undefined): string {
  if (value === undefined) return '·'
  return value.toLocaleString('en-AU')
}

/** Suburb first, then city, then the site name — never the job title. */
export function jobLocality(job: Pick<JobListItem, 'site'>): string {
  return (
    job.site?.address?.suburb ??
    job.site?.address?.city ??
    job.site?.name ??
    (job.site ? addressLine(job.site.address) : null) ??
    '—'
  )
}

/**
 * The street the job is at, without repeating the suburb.
 *
 * Imported ServiceM8 rows often only have `raw` plus a locality, so this
 * prefers the structured street and otherwise trims the suburb off the
 * imported line. `jobPlace` puts the suburb back on for the row subtitle.
 */
export function jobStreet(job: Pick<JobListItem, 'site'>): string {
  const address = job.site?.address
  if (!address) return '—'

  const street = [address.unit, address.streetNumber, address.street].filter(Boolean).join(' ')
  if (street) return street

  const raw = address.raw?.trim()
  if (!raw) return '—'

  const locality = address.suburb ?? address.city
  if (locality) {
    const cut = raw.toLowerCase().indexOf(locality.toLowerCase())
    if (cut > 0) return raw.slice(0, cut).replace(/[,\s]+$/, '') || raw
  }
  return raw
}

/** Street and suburb on one line — one address column, not two. */
export function jobPlace(job: Pick<JobListItem, 'site'>): string {
  const street = jobStreet(job)
  const locality = jobLocality(job)
  if (street === '—' && locality === '—') return '—'
  if (street === '—') return locality
  if (locality === '—') return street
  if (street.toLowerCase().includes(locality.toLowerCase())) return street
  return `${street}, ${locality}`
}

/**
 * A job title worth showing under the client. The import placeholder and a
 * title that merely repeats the client name are both dropped, so the second
 * line stays `#number · suburb` rather than a wall of "Unassigned".
 */
export function jobTitle(job: Pick<JobListItem, 'title' | 'client'>): string | null {
  const title = job.title?.trim()
  if (!title) return null
  if (title === job.client.name) return null
  if (/^unassigned\b/i.test(title)) return null
  return title
}
